import { useState, useCallback } from 'react';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number;
}

interface UseToastReturn {
  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
}

// Hook semplificato per gestire le notifiche
export const useToast = (): UseToastReturn => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: Toast = {
      ...toast,
      id,
      duration: toast.duration ?? 5000
    };

    setToasts(prev => [...prev, newToast]);

    // Auto rimuovi il toast dopo la durata specificata
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }
  }, [removeToast]);

  const showSuccess = useCallback((message: string, title?: string) => {
    showToast({ type: 'success', message, title });
  }, [showToast]);

  const showError = useCallback((message: string, title?: string) => {
    showToast({ type: 'error', message, title });
  }, [showToast]);

  const showWarning = useCallback((message: string, title?: string) => {
    showToast({ type: 'warning', message, title });
  }, [showToast]);

  const showInfo = useCallback((message: string, title?: string) => {
    showToast({ type: 'info', message, title });
  }, [showToast]);

  return {
    toasts,
    showToast,
    removeToast,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
};

// Hook alternativo per usar le notifiche in modo semplice (compatibile con il sistema esistente)
export const useSimpleToast = () => {
  const showSuccess = useCallback((message: string, title?: string) => {
    console.log(`✅ ${title ? title + ': ' : ''}${message}`);
    // TODO: Integrare con il sistema di toast esistente
  }, []);

  const showError = useCallback((message: string, title?: string) => {
    console.error(`❌ ${title ? title + ': ' : ''}${message}`);
    // TODO: Integrare con il sistema di toast esistente
  }, []);

  const showWarning = useCallback((message: string, title?: string) => {
    console.warn(`⚠️ ${title ? title + ': ' : ''}${message}`);
    // TODO: Integrare con il sistema di toast esistente
  }, []);

  const showInfo = useCallback((message: string, title?: string) => {
    console.info(`ℹ️ ${title ? title + ': ' : ''}${message}`);
    // TODO: Integrare con il sistema di toast esistente
  }, []);

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
};
